import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth, REGISTROS_FILTERS_STORAGE_KEY } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { FiPlus, FiEdit2, FiTrash2, FiCopy, FiDollarSign, FiDownload, FiFilter, FiInfo, FiFileText, FiEye, FiArrowUp, FiArrowDown, FiBriefcase, FiRotateCcw, FiChevronUp, FiChevronDown, FiMoreVertical } from 'react-icons/fi';
import { toast } from 'sonner';
import { formatBRL, maskDocument } from '../utils/format';
import { calculateRecordPriceDetails, calculateFinalPrice } from '../utils/interestCalculation';
import { SelectWithAdd } from '../components/SelectWithAdd';
import { CrudListDialog } from '../components/CrudListDialog';
import { InactiveSubscriptionCard } from '../components/InactiveSubscriptionCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const computeClientFinalValue = (record) => {
  return calculateFinalPrice(record);
};

const ALL_COLUMNS = [
  { key: 'tipo', label: 'Tipo', sortable: true, sortKey: 'record_type' },
  { key: 'descricao', label: 'Descrição', sortable: true, sortKey: 'description' },
  { key: 'valor', label: 'Valor', sortable: true, sortKey: 'value', align: 'right' },
  { key: 'pago', label: 'Pago', sortable: true, sortKey: 'is_paid', align: 'center' },
  { key: 'conciliacao', label: 'Conciliação', sortable: true, sortKey: 'reconciled', align: 'center' },
  { key: 'pagamento', label: 'Pagamento', sortable: true, sortKey: 'payment_method_name' },
  { key: 'banco', label: 'Banco', sortable: true, sortKey: 'bank_name' },
  { key: 'categoria', label: 'Categoria', sortable: true, sortKey: 'category_name' },
  { key: 'referencia', label: 'Dt. Referência', sortable: true, sortKey: 'reference_date' },
  { key: 'vencimento', label: 'Dt. Vencimento', sortable: true, sortKey: 'due_date' },
  { key: 'pagamento_data', label: 'Dt. Pagamento', sortable: true, sortKey: 'payment_date' },
  { key: 'observacao', label: 'Observação', sortable: false },
  { key: 'acoes', label: 'Ações', sortable: false, align: 'right' },
];
const DEFAULT_VISIBLE = ['tipo', 'descricao', 'valor', 'pago', 'conciliacao', 'pagamento', 'referencia', 'vencimento', 'acoes'];

const EMPTY_FORM = {
  description: '', installments: 1, value: '', is_paid: false, reconciled: false,
  record_type: 'pagar', reference_date: '', due_date: '', payment_date: '',
  payment_method_name: '', bank_name: '', category_name: '', observation: '',
  enable_interest: false,
  interest_daily_rate: '',
  max_interest_rate: '',
  show_interest_added_value: false,
  show_interest_initial_value: true,
  enable_early_discount: false,
  early_discount_daily_rate: '',
  show_early_discount_discounted_value: false,
  show_early_discount_initial_value: true,
  enable_carteira: false,
  carteira_id: '',
  carteira_name: '',
  carteira_banco_id: '',
  carteira_banco_name: '',
  carteira_group_id: '',
  carteira_group_name: '',
  carteira_category_id: '',
  carteira_account_id: '',
  carteira_account_name: '',
  conta_analitica: ''
};

export const Registros = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ADMIN_EMAILS = ['matheusrweber@gmail.com', 'keylenaiara13@gmail.com'];
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);
  const isTrial = Boolean(
    !isAdmin &&
    user?.plan_type !== 'basic' &&
    user?.plan_type !== 'essential' &&
    (user?.trial_period || user?.plan_type === 'trial' || (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date()))
  );
  const isSubscriptionActive = isAdmin || user?.subscription_status === 'active' || isTrial;
  const isBasic = !isAdmin && isSubscriptionActive && user?.plan_type === 'basic';

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [modalTab, setModalTab] = useState('geral');

  // Filters with persistence
  const [initialFilters] = useState(() => {
    const now = new Date();
    const currentMonthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const currentMonthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    const defaults = {
      startDate: currentMonthStart,
      endDate: currentMonthEnd,
      dateType: 'reference',
      statusFilter: 'all',
      typeFilter: 'all',
      paymentFilter: 'all',
      bankFilter: 'all',
      categoryFilter: 'all',
      sortKey: 'reference_date',
      sortDir: 'asc'
    };

    try {
      const storageKey = REGISTROS_FILTERS_STORAGE_KEY || 'finance_registros_filters';
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          return {
            startDate: typeof parsed.startDate === 'string' ? parsed.startDate : defaults.startDate,
            endDate: typeof parsed.endDate === 'string' ? parsed.endDate : defaults.endDate,
            dateType: typeof parsed.dateType === 'string' ? parsed.dateType : defaults.dateType,
            statusFilter: typeof parsed.statusFilter === 'string' ? parsed.statusFilter : defaults.statusFilter,
            typeFilter: typeof parsed.typeFilter === 'string' ? parsed.typeFilter : defaults.typeFilter,
            paymentFilter: typeof parsed.paymentFilter === 'string' ? parsed.paymentFilter : defaults.paymentFilter,
            bankFilter: typeof parsed.bankFilter === 'string' ? parsed.bankFilter : defaults.bankFilter,
            categoryFilter: typeof parsed.categoryFilter === 'string' ? parsed.categoryFilter : defaults.categoryFilter,
            sortKey: typeof parsed.sortKey === 'string' ? parsed.sortKey : defaults.sortKey,
            sortDir: typeof parsed.sortDir === 'string' ? parsed.sortDir : defaults.sortDir
          };
        }
      }
    } catch (_e) { /* noop */ }
    return defaults;
  });

  const [startDate, setStartDate] = useState(initialFilters.startDate);
  const [endDate, setEndDate] = useState(initialFilters.endDate);
  const [dateType, setDateType] = useState(initialFilters.dateType);
  const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter);
  const [typeFilter, setTypeFilter] = useState(initialFilters.typeFilter);
  const [paymentFilter, setPaymentFilter] = useState(initialFilters.paymentFilter);
  const [bankFilter, setBankFilter] = useState(initialFilters.bankFilter);
  const [categoryFilter, setCategoryFilter] = useState(initialFilters.categoryFilter);

  // Sort state
  const [sortKey, setSortKey] = useState(initialFilters.sortKey);
  const [sortDir, setSortDir] = useState(initialFilters.sortDir);

  // Persist filters to localStorage
  useEffect(() => {
    try {
      const storageKey = REGISTROS_FILTERS_STORAGE_KEY || 'finance_registros_filters';
      localStorage.setItem(storageKey, JSON.stringify({
        startDate,
        endDate,
        dateType,
        statusFilter,
        typeFilter,
        paymentFilter,
        bankFilter,
        categoryFilter,
        sortKey,
        sortDir
      }));
    } catch (_e) { /* noop */ }
  }, [startDate, endDate, dateType, statusFilter, typeFilter, paymentFilter, bankFilter, categoryFilter, sortKey, sortDir]);

  const handleResetFilters = () => {
    const now = new Date();
    const currentMonthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const currentMonthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    setStartDate(currentMonthStart);
    setEndDate(currentMonthEnd);
    setDateType('reference');
    setStatusFilter('all');
    setTypeFilter('all');
    setPaymentFilter('all');
    setBankFilter('all');
    setCategoryFilter('all');
    setSortKey('reference_date');
    setSortDir('asc');

    try {
      const storageKey = REGISTROS_FILTERS_STORAGE_KEY || 'finance_registros_filters';
      localStorage.removeItem(storageKey);
    } catch (_e) { /* noop */ }
  };

  // Filter card collapse state (persisted preference, default collapsed)
  const [filtersOpen, setFiltersOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('registros_filters_open');
      if (saved !== null) return saved === 'true';
    } catch (_e) { /* noop */ }
    return false;
  });

  const toggleFiltersOpen = () => {
    setFiltersOpen(prev => {
      const next = !prev;
      try {
        localStorage.setItem('registros_filters_open', String(next));
      } catch (_e) { /* noop */ }
      return next;
    });
  };

  const hasActiveFilters = useMemo(() => {
    return (
      statusFilter !== 'all' ||
      typeFilter !== 'all' ||
      paymentFilter !== 'all' ||
      bankFilter !== 'all' ||
      categoryFilter !== 'all' ||
      dateType !== 'reference'
    );
  }, [statusFilter, typeFilter, paymentFilter, bankFilter, categoryFilter, dateType]);

  // Visible columns (persisted to localStorage with automatic migration for conciliacao)
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('registros_visible_columns');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (!parsed.includes('conciliacao') && !localStorage.getItem('registros_col_migrated_conciliacao')) {
          localStorage.setItem('registros_col_migrated_conciliacao', 'true');
          const updated = [...parsed];
          const pagoIdx = updated.indexOf('pago');
          if (pagoIdx !== -1) updated.splice(pagoIdx + 1, 0, 'conciliacao');
          else updated.push('conciliacao');
          return updated;
        }
        return parsed;
      }
    } catch (_e) { /* noop */ }
    return DEFAULT_VISIBLE;
  });
  useEffect(() => { localStorage.setItem('registros_visible_columns', JSON.stringify(visibleColumns)); }, [visibleColumns]);
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);

  // Export dialogs (PDF + Excel)
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportDateType, setExportDateType] = useState('reference');
  const [exportTypeFilter, setExportTypeFilter] = useState('all');
  const [exportStatusFilter, setExportStatusFilter] = useState('all');
  const [exportGroupMode, setExportGroupMode] = useState('grouped');
  const [exportColumns, setExportColumns] = useState(DEFAULT_VISIBLE);
  const [exportColsDialogOpen, setExportColsDialogOpen] = useState(false);

  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [excelStartDate, setExcelStartDate] = useState('');
  const [excelEndDate, setExcelEndDate] = useState('');
  const [excelDateType, setExcelDateType] = useState('reference');
  const [excelFormat, setExcelFormat] = useState('xlsx');
  const [excelTypeFilter, setExcelTypeFilter] = useState('all');
  const [excelStatusFilter, setExcelStatusFilter] = useState('all');
  const [excelColumns, setExcelColumns] = useState(DEFAULT_VISIBLE);
  const [excelColsDialogOpen, setExcelColsDialogOpen] = useState(false);

  const [formData, setFormData] = useState(EMPTY_FORM);

  // Reference data (CRUD-managed: payment_methods, banks, categories)
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [carteiras, setCarteiras] = useState([]);
  const [carteiraContasTree, setCarteiraContasTree] = useState({ entradas: [], saidas: [] });
  const [newMethodName, setNewMethodName] = useState('');
  const [newBankName, setNewBankName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [payMethodDialogOpen, setPayMethodDialogOpen] = useState(false);
  const [banksDialogOpen, setBanksDialogOpen] = useState(false);
  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);

  useEffect(() => {
    if (isSubscriptionActive) {
      loadRecords();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscriptionActive, startDate, endDate, dateType, statusFilter, typeFilter, paymentFilter, bankFilter, categoryFilter]);

  useEffect(() => {
    if (isSubscriptionActive) {
      loadPaymentMethods();
      loadBanks();
      loadCategories();
      loadCarteiras();
      loadCarteiraContasTree();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscriptionActive]);

  const loadCarteiras = async () => {
    try {
      const res = await axios.get(`${API}/carteiras`, { withCredentials: true });
      setCarteiras(res.data || []);
    } catch (_e) { /* noop */ }
  };

  const loadCarteiraContasTree = async () => {
    try {
      const res = await axios.get(`${API}/carteiras/contas/tree`, { withCredentials: true });
      setCarteiraContasTree(res.data || { entradas: [], saidas: [] });
    } catch (_e) { /* noop */ }
  };

  // Contas Analíticas filtradas por Tipo de Lançamento:
  // - Contas a Receber (receber) -> Entradas (Receitas)
  // - Contas a Pagar (pagar) -> Saídas (Despesas)
  const activeContaAnaliticaGroups = useMemo(() => {
    return (formData.record_type === 'receber'
      ? carteiraContasTree?.entradas
      : carteiraContasTree?.saidas) || [];
  }, [formData.record_type, carteiraContasTree]);

  // Identificador da conta analítica selecionada (por ID ou correspondência de nome)
  const selectedContaAnaliticaId = useMemo(() => {
    if (formData.carteira_account_id) return formData.carteira_account_id;
    const nameToMatch = formData.conta_analitica || formData.carteira_account_name;
    if (!nameToMatch) return '';
    for (const grp of activeContaAnaliticaGroups) {
      const acc = (grp.accounts || []).find(a => a.name === nameToMatch);
      if (acc) return acc.account_id;
    }
    return '';
  }, [formData.carteira_account_id, formData.conta_analitica, formData.carteira_account_name, activeContaAnaliticaGroups]);

  const handleContaAnaliticaChange = (accId) => {
    let foundGroup = null;
    let foundAcc = null;
    for (const grp of activeContaAnaliticaGroups) {
      const acc = (grp.accounts || []).find(a => a.account_id === accId);
      if (acc) {
        foundGroup = grp;
        foundAcc = acc;
        break;
      }
    }

    setFormData(prev => ({
      ...prev,
      carteira_account_id: accId,
      carteira_account_name: foundAcc ? foundAcc.name : '',
      conta_analitica: foundAcc ? foundAcc.name : '',
      carteira_group_id: foundGroup ? foundGroup.group_id : '',
      carteira_group_name: foundGroup ? foundGroup.name : ''
    }));
  };

  const loadRecords = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('date_type', dateType);
      params.append('status_filter', statusFilter);
      params.append('type_filter', typeFilter);
      if (paymentFilter !== 'all') params.append('payment_method', paymentFilter);
      if (bankFilter !== 'all') params.append('bank', bankFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);

      const url = `${API}/financial-records?${params.toString()}`;
      const response = await axios.get(url, { withCredentials: true });
      setRecords(response.data);
    } catch (error) {
      console.error('Error loading records:', error);
      toast.error('Erro ao carregar registros');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const r = await axios.get(`${API}/payment-methods`, { withCredentials: true });
      setPaymentMethods(r.data);
    } catch (_e) { /* noop */ }
  };
  const loadBanks = async () => {
    try {
      const r = await axios.get(`${API}/banks`, { withCredentials: true });
      setBanks(r.data);
    } catch (_e) { /* noop */ }
  };
  const loadCategories = async () => {
    try {
      const r = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(r.data);
    } catch (_e) { /* noop */ }
  };

  const addPaymentMethod = async (payload) => {
    const name = (typeof payload === 'object' ? payload?.name : newMethodName) || newMethodName;
    const color = (typeof payload === 'object' ? payload?.color : null) || '#3B82F6';
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API}/payment-methods`, { name: name.trim(), color }, { withCredentials: true });
      setNewMethodName('');
      loadPaymentMethods();
      toast.success('Forma de pagamento criada!');
    } catch (_e) { toast.error('Erro ao criar forma de pagamento'); }
  };
  const updatePaymentMethod = async (id, updatedItem) => {
    try {
      await axios.put(`${API}/payment-methods/${id}`, updatedItem, { withCredentials: true });
      loadPaymentMethods();
      fetchRecords();
      toast.success('Forma de pagamento atualizada!');
    } catch (_e) { toast.error('Erro ao atualizar forma de pagamento'); }
  };
  const deletePaymentMethod = async (id) => {
    try {
      await axios.delete(`${API}/payment-methods/${id}`, { withCredentials: true });
      loadPaymentMethods();
      toast.success('Removida');
    } catch (_e) { toast.error('Erro ao remover'); }
  };
  const addBank = async (payload) => {
    const name = (typeof payload === 'object' ? payload?.name : newBankName) || newBankName;
    const color = (typeof payload === 'object' ? payload?.color : null) || '#1E3A8A';
    const nameToAdd = (name || '').trim();
    if (!nameToAdd) return;
    try {
      await axios.post(`${API}/banks`, { name: nameToAdd, color }, { withCredentials: true });
      setNewBankName('');
      await loadBanks();
      setFormData(prev => ({ ...prev, bank_name: nameToAdd }));
      toast.success(`Instituição "${nameToAdd}" adicionada!`);
    } catch (_e) { toast.error('Erro ao cadastrar banco'); }
  };
  const updateBank = async (id, updatedItem) => {
    try {
      await axios.put(`${API}/banks/${id}`, updatedItem, { withCredentials: true });
      loadBanks();
      fetchRecords();
      toast.success('Banco atualizado com sucesso!');
    } catch (_e) { toast.error('Erro ao atualizar banco'); }
  };
  const deleteBank = async (id) => {
    try {
      await axios.delete(`${API}/banks/${id}`, { withCredentials: true });
      loadBanks();
      toast.success('Removido');
    } catch (_e) { toast.error('Erro ao remover'); }
  };
  const addCategory = async (payload) => {
    const name = (typeof payload === 'object' ? payload?.name : newCategoryName) || newCategoryName;
    const color = (typeof payload === 'object' ? payload?.color : null) || '#10B981';
    if (!name || !name.trim()) return;
    try {
      await axios.post(`${API}/categories`, { name: name.trim(), color }, { withCredentials: true });
      setNewCategoryName('');
      loadCategories();
      toast.success('Categoria criada!');
    } catch (_e) { toast.error('Erro ao criar categoria'); }
  };
  const updateCategory = async (id, updatedItem) => {
    try {
      await axios.put(`${API}/categories/${id}`, updatedItem, { withCredentials: true });
      loadCategories();
      fetchRecords();
      toast.success('Categoria atualizada!');
    } catch (_e) { toast.error('Erro ao atualizar categoria'); }
  };
  const deleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/categories/${id}`, { withCredentials: true });
      loadCategories();
      toast.success('Removida');
    } catch (_e) { toast.error('Erro ao remover'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        value: parseFloat(String(formData.value).replace(',', '.')),
        installments: parseInt(formData.installments) || 1,
        due_date: formData.due_date || null,
        payment_date: formData.payment_date || null,
        enable_interest: isBasic ? false : Boolean(formData.enable_interest),
        interest_daily_rate: (!isBasic && formData.enable_interest) ? (parseFloat(String(formData.interest_daily_rate).replace(',', '.')) || 0) : 0,
        max_interest_rate: (!isBasic && formData.enable_interest) ? (parseFloat(String(formData.max_interest_rate).replace(',', '.')) || 0) : 0,
        enable_early_discount: isBasic ? false : Boolean(formData.enable_early_discount),
        early_discount_daily_rate: (!isBasic && formData.enable_early_discount) ? (parseFloat(String(formData.early_discount_daily_rate).replace(',', '.')) || 0) : 0,
        enable_carteira: isBasic ? false : Boolean(formData.enable_carteira),
        carteira_id: (!isBasic && formData.enable_carteira) ? (formData.carteira_id || null) : null,
        carteira_name: (!isBasic && formData.enable_carteira) ? (formData.carteira_name || null) : null,
        carteira_banco_id: (!isBasic && formData.enable_carteira) ? (formData.carteira_banco_id || null) : null,
        carteira_banco_name: (!isBasic && formData.enable_carteira) ? (formData.carteira_banco_name || null) : null,
        carteira_group_id: (!isBasic && formData.enable_carteira) ? (formData.carteira_group_id || null) : null,
        carteira_group_name: (!isBasic && formData.enable_carteira) ? (formData.carteira_group_name || null) : null,
        carteira_category_id: (!isBasic && formData.enable_carteira) ? (formData.carteira_category_id || null) : null,
        carteira_account_id: (!isBasic && formData.enable_carteira) ? (formData.carteira_account_id || null) : null,
        carteira_account_name: (!isBasic && formData.enable_carteira) ? (formData.carteira_account_name || formData.conta_analitica || null) : null,
        conta_analitica: (!isBasic && formData.enable_carteira) ? (formData.conta_analitica || formData.carteira_account_name || null) : null,
      };
      if (editingRecord) {
        await axios.put(`${API}/financial-records/${editingRecord.record_id}`, data, { withCredentials: true });
        toast.success('Registro atualizado!');
      } else {
        await axios.post(`${API}/financial-records`, data, { withCredentials: true });
        const parcelasMsg = data.installments > 1 ? ` (${data.installments} parcelas criadas)` : '';
        toast.success(`Registro criado${parcelasMsg}!`);
      }
      handleCloseDialog();
      loadRecords();
    } catch (error) {
      console.error('Error saving record:', error);
      toast.error(error?.response?.data?.detail || 'Erro ao salvar registro');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      description: record.description,
      installments: record.installments,
      value: record.value.toString(),
      is_paid: record.is_paid,
      reconciled: Boolean(record.reconciled),
      record_type: record.record_type,
      reference_date: record.reference_date,
      due_date: record.due_date || '',
      payment_date: record.payment_date || '',
      payment_method_name: record.payment_method_name || '',
      bank_name: record.bank_name || '',
      category_name: record.category_name || '',
      observation: record.observation || '',
      enable_interest: Boolean(record.enable_interest),
      interest_daily_rate: record.interest_daily_rate != null && record.interest_daily_rate !== 0 ? record.interest_daily_rate.toString() : (record.enable_interest ? '0.033' : ''),
      max_interest_rate: record.max_interest_rate != null && record.max_interest_rate !== 0 ? record.max_interest_rate.toString() : (record.enable_interest ? '2' : ''),
      show_interest_added_value: Boolean(record.show_interest_added_value),
      show_interest_initial_value: record.show_interest_initial_value !== false,
      enable_early_discount: Boolean(record.enable_early_discount),
      early_discount_daily_rate: record.early_discount_daily_rate != null && record.early_discount_daily_rate !== 0 ? record.early_discount_daily_rate.toString() : (record.enable_early_discount ? '0.033' : ''),
      show_early_discount_discounted_value: Boolean(record.show_early_discount_discounted_value),
      show_early_discount_initial_value: record.show_early_discount_initial_value !== false,
      enable_carteira: Boolean(record.enable_carteira),
      carteira_id: record.carteira_id || '',
      carteira_name: record.carteira_name || '',
      carteira_banco_id: record.carteira_banco_id || '',
      carteira_banco_name: record.carteira_banco_name || '',
      carteira_group_id: record.carteira_group_id || '',
      carteira_group_name: record.carteira_group_name || '',
      carteira_category_id: record.carteira_category_id || '',
      carteira_account_id: record.carteira_account_id || '',
      carteira_account_name: record.carteira_account_name || record.conta_analitica || '',
      conta_analitica: record.conta_analitica || record.carteira_account_name || '',
      carteira_account_id: record.carteira_account_id || '',
      carteira_account_name: record.carteira_account_name || record.conta_analitica || '',
      conta_analitica: record.conta_analitica || record.carteira_account_name || ''
    });
    setModalTab('geral');
    setOpen(true);
  };

  const handleDuplicate = (record) => {
    setEditingRecord(null);
    setFormData({
      description: record.description,
      installments: 1,
      value: record.value.toString(),
      is_paid: false,
      reconciled: false,
      record_type: record.record_type,
      reference_date: record.reference_date,
      due_date: record.due_date || '',
      payment_date: '',
      payment_method_name: record.payment_method_name || '',
      bank_name: record.bank_name || '',
      category_name: record.category_name || '',
      observation: record.observation || '',
      enable_interest: Boolean(record.enable_interest),
      interest_daily_rate: record.interest_daily_rate != null ? record.interest_daily_rate.toString() : '',
      max_interest_rate: record.max_interest_rate != null ? record.max_interest_rate.toString() : '',
      show_interest_added_value: Boolean(record.show_interest_added_value),
      show_interest_initial_value: record.show_interest_initial_value !== false,
      enable_early_discount: Boolean(record.enable_early_discount),
      early_discount_daily_rate: record.early_discount_daily_rate != null ? record.early_discount_daily_rate.toString() : '',
      show_early_discount_discounted_value: Boolean(record.show_early_discount_discounted_value),
      show_early_discount_initial_value: record.show_early_discount_initial_value !== false,
      enable_carteira: Boolean(record.enable_carteira),
      carteira_id: record.carteira_id || '',
      carteira_name: record.carteira_name || '',
      carteira_banco_id: record.carteira_banco_id || '',
      carteira_banco_name: record.carteira_banco_name || '',
      carteira_group_id: record.carteira_group_id || '',
      carteira_group_name: record.carteira_group_name || '',
      carteira_category_id: record.carteira_category_id || ''
    });
    setModalTab('geral');
    setOpen(true);
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Tem certeza que deseja deletar este registro?')) return;
    try {
      await axios.delete(`${API}/financial-records/${recordId}`, { withCredentials: true });
      toast.success('Registro deletado!');
      loadRecords();
    } catch (_e) { toast.error('Erro ao deletar registro'); }
  };

  const handleTogglePaid = async (record) => {
    try {
      await axios.put(`${API}/financial-records/${record.record_id}`, { is_paid: !record.is_paid }, { withCredentials: true });
      loadRecords();
    } catch (_e) { toast.error('Erro ao atualizar status'); }
  };

  const handleToggleReconciled = async (record) => {
    const nextState = !record.reconciled;
    try {
      setRecords(prev => prev.map(r => r.record_id === record.record_id ? { ...r, reconciled: nextState } : r));
      await axios.put(`${API}/financial-records/${record.record_id}`, { reconciled: nextState }, { withCredentials: true });
      if (nextState) {
        toast.success('Registro marcado como conciliado!');
      } else {
        toast.info('Registro marcado como pendente.');
      }
    } catch (_e) {
      toast.error('Erro ao atualizar conciliação');
      loadRecords();
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingRecord(null);
    setFormData(EMPTY_FORM);
    setModalTab('geral');
  };

  const handleDeleteAllFiltered = async () => {
    if (!records.length) return;
    if (!window.confirm(`Tem certeza que deseja apagar ${records.length} registro(s) filtrado(s)? Esta ação não pode ser desfeita.`)) return;
    try {
      const ids = records.map(r => r.record_id);
      await Promise.all(ids.map(id => axios.delete(`${API}/financial-records/${id}`, { withCredentials: true })));
      toast.success(`${ids.length} registro(s) apagado(s)`);
      loadRecords();
    } catch (_e) { toast.error('Erro ao apagar registros'); }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const getRecordValue = (r) => (r.final_value != null ? r.final_value : computeClientFinalValue(r));

  const sortedRecords = useMemo(() => {
    const arr = [...records];
    arr.sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      if (sortKey === 'value' || sortKey === 'final_value') {
        va = getRecordValue(a);
        vb = getRecordValue(b);
      }
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return va - vb;
      if (typeof va === 'boolean' && typeof vb === 'boolean') return Number(va) - Number(vb);
      return String(va).localeCompare(String(vb), 'pt-BR');
    });
    if (sortDir === 'desc') arr.reverse();
    return arr;
  }, [records, sortKey, sortDir]);

  // Summary
  const receitas = records.filter(r => r.record_type === 'receber').reduce((s, r) => s + getRecordValue(r), 0);
  const despesas = records.filter(r => r.record_type === 'pagar').reduce((s, r) => s + getRecordValue(r), 0);
  const totalAmount = receitas - despesas;
  const receitasPagas = records.filter(r => r.record_type === 'receber' && r.is_paid).reduce((s, r) => s + getRecordValue(r), 0);
  const despesasPagas = records.filter(r => r.record_type === 'pagar' && r.is_paid).reduce((s, r) => s + getRecordValue(r), 0);
  const receitasPagasPct = receitas > 0 ? (receitasPagas / receitas * 100) : 0;
  const despesasPagasPct = despesas > 0 ? (despesasPagas / despesas * 100) : 0;
  const saldoReal = receitasPagas - despesasPagas;

  const handleOpenExportPDF = () => {
    setExportStartDate(startDate);
    setExportEndDate(endDate);
    setExportDateType(dateType);
    setExportTypeFilter(typeFilter === 'all' ? 'all' : typeFilter);
    setExportStatusFilter(statusFilter === 'all' ? 'all' : statusFilter);
    setExportDialogOpen(true);
  };

  const handleExportPDF = async () => {
    if (!exportStartDate || !exportEndDate) { toast.error('Selecione a data inicial e final para exportar'); return; }
    try {
      const params = new URLSearchParams({
        start_date: exportStartDate, end_date: exportEndDate,
        date_type: exportDateType,
        type_filter: exportTypeFilter, status_filter: exportStatusFilter,
        group_mode: exportGroupMode
      });
      const response = await axios.get(`${API}/export-data-range?${params.toString()}`, { withCredentials: true });
      const { exportMonthlyReportPDF } = await import('../utils/pdfExport');
      exportMonthlyReportPDF({ ...response.data, columns: exportColumns, date_type: exportDateType });
      toast.success('PDF exportado com sucesso!');
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  const handleOpenExportExcel = () => {
    setExcelStartDate(startDate);
    setExcelEndDate(endDate);
    setExcelDateType(dateType);
    setExcelFormat('xlsx');
    setExcelTypeFilter(typeFilter === 'all' ? 'all' : typeFilter);
    setExcelStatusFilter(statusFilter === 'all' ? 'all' : statusFilter);
    setExcelDialogOpen(true);
  };
  const handleExportExcel = handleOpenExportExcel;

  const confirmExportExcel = async () => {
    if (!excelStartDate || !excelEndDate) {
      toast.error('Selecione a data inicial e final para exportar');
      return;
    }
    try {
      if (excelFormat === 'xlsx') {
        const params = new URLSearchParams({
          start_date: excelStartDate,
          end_date: excelEndDate,
          date_type: excelDateType,
          type_filter: excelTypeFilter,
          status_filter: excelStatusFilter,
          columns: excelColumns.join(',')
        });
        const response = await axios.get(`${API}/export-data-excel?${params.toString()}`, {
          withCredentials: true,
          responseType: 'blob'
        });
        const url = URL.createObjectURL(new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `Finance-Registros-${excelStartDate}-a-${excelEndDate}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        setExcelDialogOpen(false);
        toast.success('Planilha Excel (.xlsx) exportada com sucesso!');
      } else {
        const params = new URLSearchParams({
          start_date: excelStartDate,
          end_date: excelEndDate,
          date_type: excelDateType,
          type_filter: excelTypeFilter,
          status_filter: excelStatusFilter,
          group_mode: 'grouped'
        });
        const response = await axios.get(`${API}/export-data-range?${params.toString()}`, { withCredentials: true });
        const recs = response.data.records || [];
        if (!recs.length) { toast.error('Nenhum registro encontrado'); return; }

        const colDefs = {
          tipo: { label: 'Tipo', get: r => r.record_type === 'pagar' ? 'Pagar' : 'Receber' },
          descricao: { label: 'Descrição', get: r => r.description },
          valor: { label: 'Valor', get: r => (r.final_value != null ? r.final_value : computeClientFinalValue(r)).toFixed(2).replace('.', ',') },
          pago: { label: 'Pago', get: r => r.is_paid ? 'Sim' : 'Não' },
          conciliacao: { label: 'Conciliação', get: r => r.reconciled ? 'Sim' : 'Não' },
          pagamento: { label: 'Pagamento', get: r => r.payment_method_name || '' },
          banco: { label: 'Banco', get: r => r.bank_name || '' },
          categoria: { label: 'Categoria', get: r => r.category_name || '' },
          referencia: { label: 'Dt. Referência', get: r => new Date(r.reference_date + 'T00:00:00').toLocaleDateString('pt-BR') },
          vencimento: { label: 'Dt. Vencimento', get: r => r.due_date ? new Date(r.due_date + 'T00:00:00').toLocaleDateString('pt-BR') : '' },
          pagamento_data: { label: 'Dt. Pagamento', get: r => r.payment_date ? new Date(r.payment_date + 'T00:00:00').toLocaleDateString('pt-BR') : '' },
          observacao: { label: 'Observação', get: r => (r.observation || '').replace(/[\r\n;]/g, ' ') }
        };
        const cols = excelColumns.filter(c => colDefs[c]);
        const headers = cols.map(c => colDefs[c].label);
        const rows = recs.map(r => cols.map(c => colDefs[c].get(r)));
        const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Finance-Registros-${excelStartDate}-a-${excelEndDate}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        setExcelDialogOpen(false);
        toast.success('Planilha CSV exportada com sucesso!');
      }
    } catch (_e) { toast.error('Erro ao exportar'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
            <p className="mt-4 text-blue-900 font-medium">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
        <Header />
        <main className="transition-all duration-300">
          <InactiveSubscriptionCard moduleName="Registros Financeiros" />
        </main>
      </div>
    );
  }

  const colVisible = (key) => visibleColumns.includes(key);
  const toggleColumn = (key) => {
    setVisibleColumns(prev => prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key]);
  };

  const renderSortIcon = (col) => {
    if (!col.sortable) return null;
    if (sortKey !== col.sortKey) return <FiArrowUp className="opacity-25 inline ml-1" size={12} />;
    return sortDir === 'asc' ? <FiArrowUp className="inline ml-1" size={12} /> : <FiArrowDown className="inline ml-1" size={12} />;
  };

  return (
    <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors">
      <Header />

      <main className="transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-4xl font-black text-blue-900 mb-2">Registros Financeiros</h1>
            <p className="text-slate-700">Gerencie suas receitas e despesas</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {/* Unificado: Botão Exportar com Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="export-menu-button"
                  variant="outline"
                  className="inline-flex items-center gap-2 border-blue-900 text-blue-900 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-200 dark:hover:bg-blue-950/40"
                >
                  <FiDownload size={18} /> Exportar <FiChevronDown size={14} className="ml-0.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  data-testid="export-pdf-button"
                  onClick={handleOpenExportPDF}
                  className="flex items-center gap-2 cursor-pointer py-2"
                >
                  <FiDownload size={16} className="text-blue-900 dark:text-blue-400" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm">Relatório PDF</span>
                    <span className="text-[11px] text-slate-500">Documento formatado</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="export-excel-button"
                  onClick={handleOpenExportExcel}
                  className="flex items-center gap-2 cursor-pointer py-2"
                >
                  <FiFileText size={16} className="text-emerald-700 dark:text-emerald-400" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-sm">Planilha (Excel / CSV)</span>
                    <span className="text-[11px] text-slate-500">Dados estruturados</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Excel dialog */}
            <Dialog open={excelDialogOpen} onOpenChange={setExcelDialogOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-blue-900 dark:text-white">Exportar Planilha</DialogTitle>
                  <DialogDescription className="text-xs text-slate-500">Defina formato, período, filtros e colunas para exportar.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-blue-900 dark:text-blue-300 font-semibold">Formato do Arquivo</Label>
                    <Select value={excelFormat} onValueChange={setExcelFormat}>
                      <SelectTrigger data-testid="export-excel-format" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="xlsx">Excel (.xlsx) — Recomendado</SelectItem>
                        <SelectItem value="csv">CSV (.csv)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-blue-900 dark:text-blue-300 font-semibold">Data Inicial</Label>
                      <Input type="date" data-testid="excel-start-date" value={excelStartDate} onChange={(e) => setExcelStartDate(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-blue-900 dark:text-blue-300 font-semibold">Data Final</Label>
                      <Input type="date" data-testid="excel-end-date" value={excelEndDate} onChange={(e) => setExcelEndDate(e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-blue-900 dark:text-blue-300 font-semibold">Filtrar por</Label>
                    <Select value={excelDateType} onValueChange={setExcelDateType}>
                      <SelectTrigger data-testid="export-excel-date-type" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reference">Data de Referência</SelectItem>
                        <SelectItem value="due">Data de Vencimento</SelectItem>
                        <SelectItem value="payment">Data de Pagamento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-blue-900 dark:text-blue-300 font-semibold">Tipo</Label>
                    <Select value={excelTypeFilter} onValueChange={setExcelTypeFilter}>
                      <SelectTrigger data-testid="export-excel-type-filter" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="pagar">Contas a Pagar</SelectItem>
                        <SelectItem value="receber">Contas a Receber</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-blue-900 dark:text-blue-300 font-semibold">Status</Label>
                    <Select value={excelStatusFilter} onValueChange={setExcelStatusFilter}>
                      <SelectTrigger data-testid="export-excel-status-filter" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="paid">Pagas</SelectItem>
                        <SelectItem value="unpaid">Não Pagas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Button data-testid="excel-visualize-cols" type="button" variant="outline" onClick={() => setExcelColsDialogOpen(true)}
                      className="w-full inline-flex items-center justify-center gap-2 border-blue-900/30">
                      <FiEye size={16} /> Visualizar Colunas ({excelColumns.length} selecionada{excelColumns.length === 1 ? '' : 's'})
                    </Button>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={() => setExcelDialogOpen(false)} className="flex-1">Cancelar</Button>
                    <Button data-testid="export-excel-confirm" onClick={confirmExportExcel} className="flex-1 bg-blue-900 hover:bg-blue-800 text-white">Exportar</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Excel column-pick dialog */}
            <Dialog open={excelColsDialogOpen} onOpenChange={setExcelColsDialogOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-blue-900 dark:text-white">Colunas da Planilha</DialogTitle>
                  <DialogDescription className="text-xs text-slate-500">Selecione as colunas que deseja incluir no arquivo.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
                  {ALL_COLUMNS.filter(c => c.key !== 'acoes').map(c => (
                    <label key={c.key} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                      <Checkbox
                        checked={excelColumns.includes(c.key)}
                        onCheckedChange={() => setExcelColumns(prev => prev.includes(c.key) ? prev.filter(k => k !== c.key) : [...prev, c.key])}
                      />
                      <span className="text-sm text-slate-800 dark:text-slate-200">{c.label}</span>
                    </label>
                  ))}
                </div>
                <Button onClick={() => setExcelColsDialogOpen(false)} className="bg-blue-900 hover:bg-blue-800 text-white mt-2">Fechar</Button>
              </DialogContent>
            </Dialog>

            {/* PDF Export */}
            <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-blue-900 dark:text-white">Exportar Relatório PDF</DialogTitle>
                  <DialogDescription className="text-xs text-slate-500">Defina período, filtros, agrupamento e colunas do relatório.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <p className="text-sm text-slate-700 dark:text-slate-300">Configure os filtros do relatório</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-blue-900 dark:text-blue-300 font-semibold">Data Inicial</Label>
                      <Input type="date" data-testid="export-start-date" value={exportStartDate} onChange={(e) => setExportStartDate(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-blue-900 dark:text-blue-300 font-semibold">Data Final</Label>
                      <Input type="date" data-testid="export-end-date" value={exportEndDate} onChange={(e) => setExportEndDate(e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-blue-900 dark:text-blue-300 font-semibold">Filtrar por</Label>
                    <Select value={exportDateType} onValueChange={setExportDateType}>
                      <SelectTrigger data-testid="export-date-type" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reference">Data de Referência</SelectItem>
                        <SelectItem value="due">Data de Vencimento</SelectItem>
                        <SelectItem value="payment">Data de Pagamento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-blue-900 dark:text-blue-300 font-semibold">Tipo</Label>
                    <Select value={exportTypeFilter} onValueChange={setExportTypeFilter}>
                      <SelectTrigger data-testid="export-type-filter" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="pagar">Contas a Pagar</SelectItem>
                        <SelectItem value="receber">Contas a Receber</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-blue-900 dark:text-blue-300 font-semibold">Status</Label>
                    <Select value={exportStatusFilter} onValueChange={setExportStatusFilter}>
                      <SelectTrigger data-testid="export-status-filter" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="paid">Pagas</SelectItem>
                        <SelectItem value="unpaid">Não Pagas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-blue-900 dark:text-blue-300 font-semibold">Agrupamento</Label>
                    <RadioGroup value={exportGroupMode} onValueChange={setExportGroupMode} className="flex flex-col gap-2 mt-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="by_month" id="by_month" />
                        <Label htmlFor="by_month" className="cursor-pointer text-slate-700 dark:text-slate-300">Separado por mês</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="grouped" id="grouped" />
                        <Label htmlFor="grouped" className="cursor-pointer text-slate-700 dark:text-slate-300">Agrupado (tabela única)</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div>
                    <Button data-testid="pdf-visualize-cols" type="button" variant="outline" onClick={() => setExportColsDialogOpen(true)}
                      className="w-full inline-flex items-center justify-center gap-2 border-blue-900/30">
                      <FiEye size={16} /> Visualizar Colunas ({exportColumns.length} selecionada{exportColumns.length === 1 ? '' : 's'})
                    </Button>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setExportDialogOpen(false)} className="flex-1">Cancelar</Button>
                    <Button data-testid="export-pdf-confirm" onClick={handleExportPDF} className="flex-1 bg-blue-900 hover:bg-blue-800 text-white">Exportar</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* PDF column-pick dialog */}
            <Dialog open={exportColsDialogOpen} onOpenChange={setExportColsDialogOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-blue-900">Colunas do PDF</DialogTitle>
                  <DialogDescription className="text-xs text-slate-500">Selecione as colunas que aparecerão no PDF.</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
                  {ALL_COLUMNS.filter(c => c.key !== 'acoes').map(c => (
                    <label key={c.key} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-slate-50 cursor-pointer">
                      <Checkbox
                        checked={exportColumns.includes(c.key)}
                        onCheckedChange={() => setExportColumns(prev => prev.includes(c.key) ? prev.filter(k => k !== c.key) : [...prev, c.key])}
                      />
                      <span className="text-sm text-slate-800">{c.label}</span>
                    </label>
                  ))}
                </div>
                <Button onClick={() => setExportColsDialogOpen(false)} className="bg-blue-900 hover:bg-blue-800 text-white mt-2">Fechar</Button>
              </DialogContent>
            </Dialog>

            {/* New Record */}
            <Dialog open={open} onOpenChange={(o) => { if (!o) handleCloseDialog(); else setOpen(true); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-record-button" onClick={handleCloseDialog}
                  className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg transition-colors inline-flex items-center gap-2">
                  <FiPlus size={20} /> Novo Registro
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-blue-900 dark:text-white flex items-center gap-2">
                    <span>{editingRecord ? 'Editar Registro' : 'Novo Registro'}</span>
                    {isBasic && (modalTab === 'juros' || modalTab === 'carteira') && (
                      <span className="text-[10px] font-bold uppercase px-2 py-[2px] rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 shadow-xs inline-flex items-center gap-1 leading-none tracking-tight">
                        ⭐ Essencial
                      </span>
                    )}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
                    Preencha os dados do registro nas abas Geral, Outros, Juros e Carteira.
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <Tabs value={modalTab} onValueChange={setModalTab}>
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger data-testid="tab-geral" value="geral">Geral</TabsTrigger>
                      <TabsTrigger data-testid="tab-outros" value="outros">Outros</TabsTrigger>
                      <TabsTrigger data-testid="tab-juros" value="juros" className="flex items-center justify-center gap-1">
                        <span>Juros</span>
                        {isBasic && (
                          <span className="text-[9px] font-extrabold uppercase px-1 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 leading-none">
                            ⭐
                          </span>
                        )}
                      </TabsTrigger>
                      <TabsTrigger data-testid="tab-carteira" value="carteira" className="flex items-center justify-center gap-1">
                        <span>Carteira</span>
                        {isBasic && (
                          <span className="text-[9px] font-extrabold uppercase px-1 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/40 leading-none">
                            ⭐
                          </span>
                        )}
                      </TabsTrigger>
                    </TabsList>

                    {/* GERAL TAB */}
                    <TabsContent value="geral" className="space-y-4 mt-4">
                      <div>
                        <Label className="text-blue-900 font-semibold">Tipo</Label>
                        <RadioGroup
                          value={formData.record_type}
                          onValueChange={(v) => {
                            setFormData(prev => {
                              const newTreeList = (v === 'receber' ? carteiraContasTree?.entradas : carteiraContasTree?.saidas) || [];
                              const stillValid = newTreeList.some(g => (g.accounts || []).some(a => a.account_id === prev.carteira_account_id || a.name === (prev.conta_analitica || prev.carteira_account_name)));
                              return {
                                ...prev,
                                record_type: v,
                                carteira_account_id: stillValid ? prev.carteira_account_id : '',
                                carteira_account_name: stillValid ? prev.carteira_account_name : '',
                                conta_analitica: stillValid ? prev.conta_analitica : '',
                                carteira_group_id: stillValid ? prev.carteira_group_id : '',
                                carteira_group_name: stillValid ? prev.carteira_group_name : ''
                              };
                            });
                          }}
                          className="flex gap-4 mt-2">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="pagar" id="pagar" />
                            <Label htmlFor="pagar" className="cursor-pointer text-red-600 font-semibold">Conta a Pagar</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="receber" id="receber" />
                            <Label htmlFor="receber" className="cursor-pointer text-blue-900 font-semibold">Conta a Receber</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      <div>
                        <Label htmlFor="description" className="text-blue-900 font-semibold">Descrição*</Label>
                        <Input id="description" data-testid="record-description-input"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          required className="mt-1" placeholder="Ex: Compra supermercado" />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="installments" className="text-blue-900 font-semibold">Parcelas*</Label>
                          <Input id="installments" data-testid="record-installments-input"
                            type="number" min="1" value={formData.installments}
                            onChange={(e) => setFormData({ ...formData, installments: parseInt(e.target.value) || 1 })}
                            required className="mt-1" />
                        </div>
                        <div>
                          <Label htmlFor="value" className="text-blue-900 font-semibold">Valor da Parcela (R$)*</Label>
                          <Input id="value" data-testid="record-value-input" type="number" step="0.01" min="0.01"
                            value={formData.value}
                            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                            required className="mt-1" placeholder="0,00" />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <Label htmlFor="reference_date" className="text-blue-900 font-semibold">Data de Referência*</Label>
                          <Popover>
                            <PopoverTrigger><FiInfo className="text-slate-500 hover:text-blue-900 cursor-help" size={16} /></PopoverTrigger>
                            <PopoverContent className="text-sm">
                              <p className="font-semibold text-blue-900 mb-1">O que é Data de Referência?</p>
                              <p>É a data para quando você <strong>deseja pagar</strong> ou <strong>receber</strong>. Útil para planejar e filtrar por período.</p>
                              <p className="mt-2 text-xs text-slate-600">Se parcelado, as próximas parcelas serão nos próximos meses.</p>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <Input id="reference_date" data-testid="record-reference-date-input" type="date"
                          value={formData.reference_date}
                          onChange={(e) => setFormData({ ...formData, reference_date: e.target.value })}
                          required className="mt-1" />
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <Label htmlFor="due_date" className="text-blue-900 font-semibold">Data de Vencimento</Label>
                          <Popover>
                            <PopoverTrigger><FiInfo className="text-slate-500 hover:text-blue-900 cursor-help" size={16} /></PopoverTrigger>
                            <PopoverContent className="text-sm">
                              <p className="font-semibold text-blue-900 mb-1">Aviso de Vencimento</p>
                              <p>Não será enviada notificação de aviso de vencimento se este campo estiver em branco.</p>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <Input id="due_date" data-testid="record-due-date-input" type="date"
                          value={formData.due_date || ''}
                          onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                          className="mt-1" />
                      </div>

                      <div className="flex items-center justify-between py-1">
                        <div className="flex items-center space-x-2">
                          <Checkbox id="is_paid" data-testid="record-paid-checkbox"
                            checked={formData.is_paid}
                            onCheckedChange={(c) => setFormData({ ...formData, is_paid: c })} />
                          <Label htmlFor="is_paid" className="text-blue-900 font-semibold cursor-pointer">Pago</Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Label htmlFor="reconciled" className="text-blue-900 font-semibold cursor-pointer text-sm">
                            Conciliado
                          </Label>
                          <Switch
                            id="reconciled"
                            data-testid="record-reconciled-switch"
                            checked={Boolean(formData.reconciled)}
                            onCheckedChange={(c) => setFormData({ ...formData, reconciled: c })}
                            className="data-[state=checked]:bg-[#00E676] data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-700 transition-colors"
                          />
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600 mb-1">Total:</p>
                        <p className="text-2xl font-bold text-blue-900">
                          {formatBRL(parseFloat(formData.value || 0) * (formData.installments || 1))}
                        </p>
                        {formData.installments > 1 && (
                          <p className="text-xs text-slate-600 mt-1">
                            {formData.installments} parcelas de {formatBRL(parseFloat(formData.value || 0))}
                          </p>
                        )}
                      </div>
                    </TabsContent>

                    {/* OUTROS TAB */}
                    <TabsContent value="outros" className="space-y-4 mt-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <Label htmlFor="payment_date" className="text-blue-900 font-semibold">Data de Pagamento</Label>
                          <Popover>
                            <PopoverTrigger><FiInfo className="text-slate-500 hover:text-blue-900 cursor-help" size={16} /></PopoverTrigger>
                            <PopoverContent className="text-sm">
                              <p className="font-semibold text-blue-900 mb-1">O que é Data de Pagamento?</p>
                              <p>É a data em que o pagamento foi <strong>efetivamente realizado</strong>. Preenchida automaticamente ao marcar como "Pago", mas você pode informar manualmente.</p>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <Input id="payment_date" data-testid="record-payment-date-input" type="date"
                          value={formData.payment_date}
                          onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                          className="mt-1" />
                      </div>

                      <SelectWithAdd
                        label="Forma de Pagamento"
                        value={formData.payment_method_name}
                        onChange={(v) => setFormData({ ...formData, payment_method_name: v })}
                        options={paymentMethods.map(p => ({ id: p.method_id, name: p.name, color: p.color || '#3B82F6' }))}
                        onManage={() => setPayMethodDialogOpen(true)}
                        testIdSelect="record-payment-method"
                        testIdManage="manage-payment-method-button"
                      />

                      <SelectWithAdd
                        label="Banco"
                        value={formData.bank_name}
                        onChange={(v) => setFormData({ ...formData, bank_name: v })}
                        options={banks.map(b => ({ id: b.bank_id, name: b.name, color: b.color || '#1E3A8A' }))}
                        onManage={() => setBanksDialogOpen(true)}
                        testIdSelect="record-bank"
                        testIdManage="manage-bank-button"
                      />

                      <SelectWithAdd
                        label="Categoria"
                        value={formData.category_name}
                        onChange={(v) => setFormData({ ...formData, category_name: v })}
                        options={categories.map(c => ({ id: c.category_id, name: c.name, color: c.color || '#10B981' }))}
                        onManage={() => setCategoriesDialogOpen(true)}
                        testIdSelect="record-category"
                        testIdManage="manage-category-button"
                      />

                      <div>
                        <Label htmlFor="observation" className="text-blue-900 font-semibold">Observação</Label>
                        <Textarea
                          id="observation" data-testid="record-observation"
                          value={formData.observation}
                          maxLength={200}
                          onChange={(e) => setFormData({ ...formData, observation: e.target.value })}
                          placeholder="Notas adicionais (máx 200 caracteres)"
                          className="mt-1"
                          rows={3}
                        />
                        <p className="text-xs text-slate-500 mt-1 text-right">{(formData.observation || '').length}/200</p>
                      </div>
                    </TabsContent>

                    {/* JUROS TAB */}
                    <TabsContent value="juros" className="space-y-5 mt-4">
                      {isBasic ? (
                        <div className="p-6 bg-amber-50 dark:bg-amber-950/30 border border-amber-500/30 rounded-xl text-center space-y-3 my-4">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto font-bold">
                            %
                          </div>
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 max-w-sm mx-auto">
                            Faça o upgrade para a Assinatura Essencial para usar os registros calculados com juros.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => navigate('/assinatura')}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-sm"
                          >
                            Fazer Upgrade para o Essencial
                          </Button>
                        </div>
                      ) : (
                        <>
                          {/* Seção: Cálculo de Juros */}
                          <div className="space-y-4">
                        <div className="flex items-center justify-between border-b pb-2">
                          <h4 className="text-base font-bold text-blue-900">Cálculo de Juros</h4>
                          <div className="flex items-center space-x-2">
                            <Switch
                              id="enable_interest"
                              data-testid="toggle-enable-interest"
                              checked={Boolean(formData.enable_interest)}
                              onCheckedChange={(checked) => setFormData(prev => ({
                                ...prev,
                                enable_interest: checked,
                                interest_daily_rate: checked && (!prev.interest_daily_rate || prev.interest_daily_rate === '0') ? '0.033' : prev.interest_daily_rate,
                                max_interest_rate: checked && (!prev.max_interest_rate || prev.max_interest_rate === '0') ? '2' : prev.max_interest_rate
                              }))}
                            />
                            <Label htmlFor="enable_interest" className="text-xs font-semibold text-slate-700 cursor-pointer">
                              Ativar/desativar cálculo de juros
                            </Label>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <div className="flex items-center gap-1.5 mb-1">
                              <Label htmlFor="interest_daily_rate" className="text-blue-900 font-semibold text-sm">
                                Juros ao dia %
                              </Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button type="button" className="inline-flex text-slate-400 hover:text-blue-900 transition-colors cursor-help" title="A lei só permite cobrar no máximo 1% ao mês, ou seja, 0,033% ao dia.">
                                    <FiInfo size={15} />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="text-xs max-w-[260px] p-3 shadow-md bg-white border border-slate-200">
                                  <p className="font-semibold text-blue-900 mb-1">Juros ao dia %</p>
                                  <p className="text-slate-600">A lei só permite cobrar no máximo 1% ao mês, ou seja, 0,033% ao dia.</p>
                                </PopoverContent>
                              </Popover>
                            </div>
                            <Input
                              id="interest_daily_rate"
                              data-testid="record-interest-daily-rate"
                              type="number"
                              step="0.001"
                              min="0"
                              disabled={!formData.enable_interest}
                              value={formData.interest_daily_rate}
                              onChange={(e) => setFormData({ ...formData, interest_daily_rate: e.target.value })}
                              placeholder="0,033"
                              className="mt-1 disabled:opacity-50 disabled:bg-slate-100"
                            />
                          </div>

                          <div>
                            <div className="flex items-center gap-1.5 mb-1">
                              <Label htmlFor="max_interest_rate" className="text-blue-900 font-semibold text-sm">
                                Calcula juros até quanto?
                              </Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button type="button" className="inline-flex text-slate-400 hover:text-blue-900 transition-colors cursor-help" title="A lei limita a multa por atraso a no máximo 2% do valor do débito.">
                                    <FiInfo size={15} />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="text-xs max-w-[260px] p-3 shadow-md bg-white border border-slate-200">
                                  <p className="font-semibold text-blue-900 mb-1">Limite de Multa/Juros</p>
                                  <p className="text-slate-600">A lei limita a multa por atraso a no máximo 2% do valor do débito.</p>
                                </PopoverContent>
                              </Popover>
                            </div>
                            <Input
                              id="max_interest_rate"
                              data-testid="record-max-interest-rate"
                              type="number"
                              step="0.01"
                              min="0"
                              disabled={!formData.enable_interest}
                              value={formData.max_interest_rate}
                              onChange={(e) => setFormData({ ...formData, max_interest_rate: e.target.value })}
                              placeholder="2"
                              className="mt-1 disabled:opacity-50 disabled:bg-slate-100"
                            />
                          </div>
                        </div>

                        {/* Flags de visualização do Juros */}
                        <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row gap-4">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id="show_interest_added_value"
                              data-testid="toggle-show-interest-added-value"
                              checked={Boolean(formData.show_interest_added_value)}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_interest_added_value: checked }))}
                            />
                            <Label htmlFor="show_interest_added_value" className="text-xs text-slate-700 cursor-pointer flex items-center gap-1.5">
                              <span>Exibir tag de acréscimo</span>
                              <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                +R$ 0,00
                              </span>
                            </Label>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Switch
                              id="show_interest_initial_value"
                              data-testid="toggle-show-interest-initial-value"
                              checked={formData.show_interest_initial_value !== false}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_interest_initial_value: checked }))}
                            />
                            <Label htmlFor="show_interest_initial_value" className="text-xs text-slate-700 cursor-pointer flex items-center gap-1.5">
                              <span>Exibir preço original riscado</span>
                              <span className="text-[10px] text-slate-400 line-through">
                                R$ 0,00
                              </span>
                            </Label>
                          </div>
                        </div>
                      </div>

                      {/* Linha divisória de separação */}
                      <div className="border-t border-slate-200 my-4" />

                      {/* Seção: Pagamento Antecipado */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between border-b pb-2">
                          <h4 className="text-base font-bold text-blue-900">Pagamento Antecipado</h4>
                          <div className="flex items-center space-x-2">
                            <Switch
                              id="enable_early_discount"
                              data-testid="toggle-enable-early-discount"
                              checked={Boolean(formData.enable_early_discount)}
                              onCheckedChange={(checked) => setFormData(prev => ({
                                ...prev,
                                enable_early_discount: checked,
                                early_discount_daily_rate: checked && (!prev.early_discount_daily_rate || prev.early_discount_daily_rate === '0') ? '0.033' : prev.early_discount_daily_rate
                              }))}
                            />
                            <Label htmlFor="enable_early_discount" className="text-xs font-semibold text-slate-700 cursor-pointer">
                              Ativar cálculo de pagamento antecipado
                            </Label>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="early_discount_daily_rate" className="text-blue-900 font-semibold text-sm">
                              Desconto por dia %
                            </Label>
                            <Input
                              id="early_discount_daily_rate"
                              data-testid="record-early-discount-rate"
                              type="number"
                              step="0.001"
                              min="0"
                              disabled={!formData.enable_early_discount}
                              value={formData.early_discount_daily_rate}
                              onChange={(e) => setFormData({ ...formData, early_discount_daily_rate: e.target.value })}
                              placeholder="0,033"
                              className="mt-1 disabled:opacity-50 disabled:bg-slate-100"
                            />
                          </div>
                        </div>

                        {/* Flags de visualização do Desconto */}
                        <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row gap-4">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id="show_early_discount_discounted_value"
                              data-testid="toggle-show-early-discount-discounted-value"
                              checked={Boolean(formData.show_early_discount_discounted_value)}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_early_discount_discounted_value: checked }))}
                            />
                            <Label htmlFor="show_early_discount_discounted_value" className="text-xs text-slate-700 cursor-pointer flex items-center gap-1.5">
                              <span>Exibir tag de desconto</span>
                              <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                                -R$ 0,00
                              </span>
                            </Label>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Switch
                              id="show_early_discount_initial_value"
                              data-testid="toggle-show-early-discount-initial-value"
                              checked={formData.show_early_discount_initial_value !== false}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_early_discount_initial_value: checked }))}
                            />
                            <Label htmlFor="show_early_discount_initial_value" className="text-xs text-slate-700 cursor-pointer flex items-center gap-1.5">
                              <span>Exibir preço original riscado</span>
                              <span className="text-[10px] text-slate-400 line-through">
                                R$ 0,00
                              </span>
                            </Label>
                          </div>
                        </div>
                      </div>

                      {/* Simulação em tempo real */}
                      {(formData.enable_interest || formData.enable_early_discount) && (
                        <div className="bg-slate-50 rounded-lg p-4 space-y-1.5 mt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-600">
                              Simulação de Preço Final
                            </span>
                            <span className="text-2xl font-bold text-blue-900">
                              {formatBRL(calculateRecordPriceDetails({
                                value: formData.value,
                                due_date: formData.due_date,
                                payment_date: formData.payment_date,
                                is_paid: formData.is_paid,
                                enable_interest: formData.enable_interest,
                                interest_daily_rate: formData.interest_daily_rate,
                                max_interest_rate: formData.max_interest_rate,
                                enable_early_discount: formData.enable_early_discount,
                                early_discount_daily_rate: formData.early_discount_daily_rate
                              }).finalValue)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600">
                            {calculateRecordPriceDetails({
                              value: formData.value,
                              due_date: formData.due_date,
                              payment_date: formData.payment_date,
                              is_paid: formData.is_paid,
                              enable_interest: formData.enable_interest,
                              interest_daily_rate: formData.interest_daily_rate,
                              max_interest_rate: formData.max_interest_rate,
                              enable_early_discount: formData.enable_early_discount,
                              early_discount_daily_rate: formData.early_discount_daily_rate
                            }).statusMessage}
                          </p>
                        </div>
                      )}
                        </>
                      )}
                    </TabsContent>

                    {/* CARTEIRA TAB */}
                    <TabsContent value="carteira" className="space-y-4 mt-4">
                      {isBasic ? (
                        <div className="p-6 bg-amber-50 dark:bg-amber-950/30 border border-amber-500/30 rounded-xl text-center space-y-3 my-4">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center mx-auto font-bold">
                            <FiBriefcase size={20} />
                          </div>
                          <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                            Recurso Exclusivo Essencial
                          </span>
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 max-w-sm mx-auto">
                            Faça o upgrade para a Assinatura Essencial para vincular registros a carteiras, bancos vinculados e plano de contas.
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => navigate('/assinatura')}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-sm"
                          >
                            Fazer Upgrade para o Essencial
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between border-b pb-3">
                            <div>
                              <h4 className="text-base font-bold text-blue-900">Integração com Carteiras</h4>
                              <p className="text-xs text-slate-500">Vincule este lançamento a uma carteira e conta bancária para sincronizar o saldo em tempo real.</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch
                                id="enable_carteira"
                                data-testid="toggle-enable-carteira"
                                checked={Boolean(formData.enable_carteira)}
                                onCheckedChange={(checked) => {
                                  setFormData(prev => {
                                    const firstWallet = carteiras[0];
                                    const firstBank = firstWallet?.bancos?.[0];
                                    return {
                                      ...prev,
                                      enable_carteira: checked,
                                      carteira_id: checked ? (prev.carteira_id || firstWallet?.carteira_id || '') : '',
                                      carteira_name: checked ? (prev.carteira_name || firstWallet?.nome || '') : '',
                                      carteira_banco_id: checked ? (prev.carteira_banco_id || firstBank?.banco_item_id || '') : '',
                                      carteira_banco_name: checked ? (prev.carteira_banco_name || firstBank?.bank_name || '') : ''
                                    };
                                  });
                                }}
                              />
                              <Label htmlFor="enable_carteira" className="text-xs font-semibold text-slate-700 cursor-pointer">
                                Ativar Carteira
                              </Label>
                            </div>
                          </div>

                          {formData.enable_carteira ? (
                            <div className="space-y-4 pt-1">
                              {/* Select Carteira */}
                              <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                  Carteira (Pessoa Física / Jurídica)
                                </Label>
                                {carteiras.length > 0 ? (
                                  <Select
                                    value={formData.carteira_id}
                                    onValueChange={(val) => {
                                      const wallet = carteiras.find(c => c.carteira_id === val);
                                      const firstBank = wallet?.bancos?.[0];
                                      setFormData(prev => ({
                                        ...prev,
                                        carteira_id: val,
                                        carteira_name: wallet?.nome || '',
                                        carteira_banco_id: firstBank?.banco_item_id || '',
                                        carteira_banco_name: firstBank?.bank_name || ''
                                      }));
                                    }}
                                  >
                                    <SelectTrigger className="mt-1">
                                      <SelectValue placeholder="Selecione a carteira" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {carteiras.map((c) => (
                                        <SelectItem key={c.carteira_id} value={c.carteira_id}>
                                          <span className="font-semibold">{c.tipo} - {c.nome}</span> ({maskDocument(c.documento, c.tipo)})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <p className="text-xs text-amber-600 mt-1">Nenhuma carteira cadastrada. Acesse o menu Carteiras para cadastrar.</p>
                                )}
                              </div>

                              {/* Select Banco Vinculado (filtered to chosen wallet) */}
                              <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                  Banco Vinculado
                                </Label>
                                {(() => {
                                  const currentWallet = carteiras.find(c => c.carteira_id === formData.carteira_id);
                                  const walletBanks = currentWallet?.bancos || [];

                                  return (
                                    <Select
                                      value={formData.carteira_banco_id}
                                      onValueChange={(val) => {
                                        const b = walletBanks.find(item => item.banco_item_id === val);
                                        setFormData(prev => ({
                                          ...prev,
                                          carteira_banco_id: val,
                                          carteira_banco_name: b?.bank_name || '',
                                          bank_name: b?.bank_name || prev.bank_name
                                        }));
                                      }}
                                    >
                                      <SelectTrigger className="mt-1">
                                        <SelectValue placeholder={walletBanks.length > 0 ? "Selecione o banco da carteira" : "Nenhum banco nesta carteira"} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {walletBanks.map((b) => (
                                          <SelectItem key={b.banco_item_id} value={b.banco_item_id}>
                                            {b.bank_name} {b.conta ? `(CC: ${b.conta})` : ''} {b.saldo_atual != null ? `— Saldo: ${formatBRL(b.saldo_atual)}` : ''}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  );
                                })()}
                              </div>

                              {/* Select Conta Analítica em Árvore (Agrupado por Grupo/Categoria - apenas filhos selecionáveis) */}
                              <div>
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                    Conta Analítica
                                  </Label>
                                  <span className={`text-[11px] font-semibold ${formData.record_type === 'receber' ? 'text-emerald-600' : 'text-red-500'}`}>
                                    {formData.record_type === 'receber' ? '● Filtro: Entrada (Receitas)' : '● Filtro: Saída (Despesas)'}
                                  </span>
                                </div>

                                {activeContaAnaliticaGroups.length > 0 ? (
                                  <Select
                                    value={selectedContaAnaliticaId}
                                    onValueChange={handleContaAnaliticaChange}
                                  >
                                    <SelectTrigger className="mt-1" data-testid="select-conta-analitica">
                                      <SelectValue placeholder="Selecione a conta analítica" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-64">
                                      {activeContaAnaliticaGroups.map((grp) => (
                                        <SelectGroup key={grp.group_id} className="py-1">
                                          <SelectLabel className="px-2.5 py-1.5 text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-100/90 rounded-md my-1 flex items-center gap-1.5 select-none pointer-events-none cursor-not-allowed">
                                            <span
                                              className="w-2.5 h-2.5 rounded-full shrink-0"
                                              style={{ backgroundColor: grp.color || (formData.record_type === 'receber' ? '#10B981' : '#EF4444') }}
                                            />
                                            <span className="truncate">{grp.name}</span>
                                          </SelectLabel>
                                          {grp.accounts && grp.accounts.length > 0 ? (
                                            grp.accounts.map((acc) => (
                                              <SelectItem
                                                key={acc.account_id}
                                                value={acc.account_id}
                                                className="pl-6 font-medium cursor-pointer"
                                              >
                                                ↳ {acc.name}
                                              </SelectItem>
                                            ))
                                          ) : (
                                            <div className="text-[11px] text-slate-400 pl-6 py-1 italic select-none pointer-events-none">
                                              (Nenhuma conta cadastrada neste grupo)
                                            </div>
                                          )}
                                        </SelectGroup>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 mt-1">
                                    Nenhuma conta de {formData.record_type === 'receber' ? 'Entrada (Receita)' : 'Saída (Despesa)'} encontrada. Cadastre contas no <strong>Menu Carteiras &gt; Contas</strong>.
                                  </div>
                                )}
                              </div>

                              <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-800">
                                💡 <strong>Sincronização Automática:</strong> Ao salvar este registro com a carteira ativada, a movimentação será vinculada à conta bancária selecionada e atualizará o saldo em tempo real quando estiver paga/conciliada.
                              </div>
                            </div>
                          ) : (
                            <div className="p-6 bg-slate-50 rounded-xl text-center text-slate-500 text-xs border border-dashed border-slate-200">
                              A chave de Carteira está desativada. Ative a chave acima se desejar vincular este registro a uma Carteira e Conta Bancária específica.
                            </div>
                          )}
                        </>
                      )}
                    </TabsContent>
                  </Tabs>

                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1">Cancelar</Button>
                    <Button data-testid="save-record-button" type="submit" className="flex-1 bg-blue-900 hover:bg-blue-800 text-white">
                      {editingRecord ? 'Atualizar' : 'Criar'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Manage Payment Methods Dialog */}
            <CrudListDialog
              open={payMethodDialogOpen} onOpenChange={setPayMethodDialogOpen}
              title="Formas de Pagamento" placeholder="Nome da forma de pagamento"
              newValue={newMethodName} onNewValueChange={setNewMethodName}
              onAdd={addPaymentMethod}
              items={paymentMethods.map(p => ({ id: p.method_id, name: p.name, color: p.color || '#3B82F6' }))}
              onUpdate={updatePaymentMethod}
              onDelete={deletePaymentMethod}
              defaultColor="#3B82F6"
              testIdPrefix="payment-method"
            />
            <CrudListDialog
              open={banksDialogOpen} onOpenChange={setBanksDialogOpen}
              title="Bancos" placeholder="Nome do banco"
              newValue={newBankName} onNewValueChange={setNewBankName}
              onAdd={addBank}
              items={banks.map(b => ({ id: b.bank_id, name: b.name, color: b.color || '#1E3A8A' }))}
              onUpdate={updateBank}
              onDelete={deleteBank}
              defaultColor="#1E3A8A"
              testIdPrefix="bank"
            />
            <CrudListDialog
              open={categoriesDialogOpen} onOpenChange={setCategoriesDialogOpen}
              title="Categorias" placeholder="Nome da categoria"
              newValue={newCategoryName} onNewValueChange={setNewCategoryName}
              onAdd={addCategory}
              items={categories.map(c => ({ id: c.category_id, name: c.name, color: c.color || '#10B981' }))}
              onUpdate={updateCategory}
              onDelete={deleteCategory}
              defaultColor="#10B981"
              testIdPrefix="category"
            />
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-slate-50 border border-blue-900/10 rounded-lg p-4 mb-6 transition-all shadow-sm">
          <div className="flex items-center justify-between">
            <button
              type="button"
              data-testid="toggle-filters-button"
              onClick={toggleFiltersOpen}
              className="flex items-center gap-2 group select-none text-left cursor-pointer focus:outline-none"
              title={filtersOpen ? "Ocultar filtros" : "Mostrar filtros"}
            >
              <FiFilter className="text-blue-900" size={20} />
              <Label className="text-blue-900 font-semibold cursor-pointer group-hover:text-blue-700 transition-colors">
                Filtros
              </Label>
              {filtersOpen ? (
                <FiChevronUp className="text-blue-900 group-hover:text-blue-700 transition-transform" size={18} />
              ) : (
                <FiChevronDown className="text-blue-900 group-hover:text-blue-700 transition-transform" size={18} />
              )}
              {!filtersOpen && hasActiveFilters && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-800 font-medium px-2 py-0.5 rounded-full">
                  Filtros ativos
                </span>
              )}
            </button>
            <div className="flex items-center gap-2">
              <Button
                data-testid="limpar-filtros-button"
                type="button"
                variant="outline"
                size="sm"
                onClick={handleResetFilters}
                className="inline-flex items-center gap-1.5 border-blue-900/30 text-blue-900 hover:bg-blue-50"
                title="Restaurar filtros para o padrão do mês atual"
              >
                <FiRotateCcw size={14} /> Limpar Filtros
              </Button>
              <Button
                data-testid="visualizar-cols-button"
                variant="outline" size="sm"
                onClick={() => setColumnsDialogOpen(true)}
                className="inline-flex items-center gap-2 border-blue-900/30 text-blue-900 hover:bg-blue-50"
              >
                <FiEye size={16} /> Visualizar
              </Button>
            </div>
          </div>

          {filtersOpen && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-200">
              <div>
                <Label className="text-sm text-slate-700">Data Inicial</Label>
                <Input type="date" data-testid="filter-start-date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Data Final</Label>
                <Input type="date" data-testid="filter-end-date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Filtrar por</Label>
                <Select value={dateType} onValueChange={setDateType}>
                  <SelectTrigger data-testid="filter-date-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reference">Data de Referência</SelectItem>
                    <SelectItem value="due">Data de Vencimento</SelectItem>
                    <SelectItem value="payment">Data de Pagamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="filter-status" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="paid">Pagos</SelectItem>
                    <SelectItem value="unpaid">Não Pagos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Tipo</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="filter-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pagar">A Pagar</SelectItem>
                    <SelectItem value="receber">A Receber</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Pagamento</Label>
                <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                  <SelectTrigger data-testid="filter-payment" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {paymentMethods.map(pm => <SelectItem key={pm.method_id} value={pm.name}>{pm.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Banco</Label>
                <Select value={bankFilter} onValueChange={setBankFilter}>
                  <SelectTrigger data-testid="filter-bank" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {banks.map(b => <SelectItem key={b.bank_id} value={b.name}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Categoria</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger data-testid="filter-category" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categories.map(c => <SelectItem key={c.category_id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Visualizar (column visibility) dialog */}
        <Dialog open={columnsDialogOpen} onOpenChange={setColumnsDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-blue-900">Visualizar Colunas</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">Escolha quais colunas aparecem na tabela. Sua preferência é salva no navegador.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {ALL_COLUMNS.map(c => (
                <label key={c.key} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-slate-50 cursor-pointer">
                  <Checkbox
                    data-testid={`col-toggle-${c.key}`}
                    checked={visibleColumns.includes(c.key)}
                    onCheckedChange={() => toggleColumn(c.key)}
                  />
                  <span className="text-sm text-slate-800">{c.label}</span>
                </label>
              ))}
            </div>
            <Button onClick={() => setColumnsDialogOpen(false)} className="bg-blue-900 hover:bg-blue-800 text-white mt-2">Fechar</Button>
          </DialogContent>
        </Dialog>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-blue-900/10 shadow-sm rounded-lg p-6">
            <p className="text-sm font-bold uppercase tracking-wider text-blue-900/70 mb-2">Receitas</p>
            <p className="text-3xl font-black text-blue-900">{formatBRL(receitas)}</p>
            <p className="text-xs text-slate-500 mt-1">Pago: {formatBRL(receitasPagas)} ({receitasPagasPct.toFixed(0)}%)</p>
          </div>
          <div className="bg-white border border-red-600/10 shadow-sm rounded-lg p-6">
            <p className="text-sm font-bold uppercase tracking-wider text-red-600/70 mb-2">Despesas</p>
            <p className="text-3xl font-black text-red-600">{formatBRL(despesas)}</p>
            <p className="text-xs text-slate-500 mt-1">Pago: {formatBRL(despesasPagas)} ({despesasPagasPct.toFixed(0)}%)</p>
          </div>
          <div className="bg-white border border-blue-900/10 shadow-sm rounded-lg p-6">
            <p className="text-sm font-bold uppercase tracking-wider text-blue-900/70 mb-2">Saldo</p>
            <p className={`text-3xl font-black ${totalAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatBRL(totalAmount)}</p>
            <p className={`text-xs mt-1 ${saldoReal >= 0 ? 'text-green-600' : 'text-red-600'}`}>Saldo real: {formatBRL(saldoReal)}</p>
          </div>
        </div>

        <div className="flex justify-end mb-2">
          {records.length > 0 && (
            <button data-testid="delete-all-filtered" onClick={handleDeleteAllFiltered}
              className="text-xs text-slate-400 hover:text-red-600 transition-colors">
              Apagar tudo ({records.length})
            </button>
          )}
        </div>

        <div className="bg-white border border-blue-900/10 shadow-sm rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {ALL_COLUMNS.filter(c => colVisible(c.key)).map(c => (
                  <TableHead
                    key={c.key}
                    data-testid={`col-header-${c.key}`}
                    className={`font-bold text-blue-900 ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''} ${c.sortable ? 'cursor-pointer select-none hover:bg-blue-50' : ''}`}
                    onClick={() => c.sortable && handleSort(c.sortKey)}
                  >
                    {c.label}{renderSortIcon(c)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="text-center py-12">
                    <FiDollarSign className="mx-auto text-slate-400 mb-3" size={48} />
                    <p className="text-slate-600 font-medium">Nenhum registro encontrado</p>
                    <p className="text-slate-500 text-sm">Clique em "Novo Registro" para começar</p>
                  </TableCell>
                </TableRow>
              ) : (
                sortedRecords.map((record) => {
                  const priceDetails = calculateRecordPriceDetails(record);
                  return (
                    <TableRow
                      key={record.record_id}
                      data-testid={`record-row-${record.record_id}`}
                      className={record.record_type === 'pagar' ? 'border-l-4 border-l-red-600' : 'border-l-4 border-l-blue-900'}
                    >
                      {colVisible('tipo') && (
                        <TableCell>
                          <span className={`text-xs font-bold px-2 py-1 rounded ${record.record_type === 'pagar' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                            {record.record_type === 'pagar' ? 'Pagar' : 'Receber'}
                          </span>
                        </TableCell>
                      )}
                      {colVisible('descricao') && <TableCell className="font-medium text-slate-900 dark:text-white">{record.description}</TableCell>}
                      {colVisible('valor') && (
                        <TableCell className="text-right">
                          {(() => {
                            const isInterest = priceDetails.adjustmentType === 'interest' && priceDetails.adjustmentAmount > 0;
                            const isDiscount = priceDetails.adjustmentType === 'discount' && priceDetails.adjustmentAmount > 0;
                            const showInitial = isInterest
                              ? (record.show_interest_initial_value !== false)
                              : (isDiscount ? (record.show_early_discount_initial_value !== false) : false);
                            const showAdjusted = isInterest
                              ? Boolean(record.show_interest_added_value)
                              : (isDiscount ? Boolean(record.show_early_discount_discounted_value) : false);
                            const hasVisibleAdjustment = priceDetails.hasAdjustment && (showInitial || showAdjusted);

                            return (
                              <div>
                                <div className={`font-semibold ${record.record_type === 'pagar' ? 'text-red-600' : 'text-blue-900'}`}>
                                  {formatBRL(priceDetails.finalValue)}
                                </div>
                                {hasVisibleAdjustment && (
                                  <div className="flex items-center justify-end gap-1 mt-0.5" title={priceDetails.tooltip}>
                                    {showAdjusted && (
                                      isInterest ? (
                                        <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 whitespace-nowrap">
                                          {`+${formatBRL(priceDetails.adjustmentAmount)}`}
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 whitespace-nowrap">
                                          {`-${formatBRL(priceDetails.adjustmentAmount)}`}
                                        </span>
                                      )
                                    )}
                                    {showInitial && (
                                      <span className="text-[10px] text-slate-400 line-through whitespace-nowrap">
                                        {formatBRL(priceDetails.baseValue)}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                      )}
                    {colVisible('pago') && (
                      <TableCell className="text-center">
                        <Checkbox data-testid={`record-paid-toggle-${record.record_id}`} checked={record.is_paid} onCheckedChange={() => handleTogglePaid(record)} />
                      </TableCell>
                    )}
                    {colVisible('conciliacao') && (
                      <TableCell className="text-center">
                        <div
                          title={record.reconciled ? "Registro conciliado. Clique para desconciliar manualmente" : "Registro pendente. Clique para marcar como conciliado"}
                          className="inline-flex items-center justify-center"
                        >
                          <Switch
                            data-testid={`record-reconciled-toggle-${record.record_id}`}
                            checked={Boolean(record.reconciled)}
                            onCheckedChange={() => handleToggleReconciled(record)}
                            className="data-[state=checked]:bg-[#00E676] data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-700 transition-colors"
                          />
                        </div>
                      </TableCell>
                    )}
                    {colVisible('pagamento') && <TableCell className="text-xs text-slate-600">{record.payment_method_name || '-'}</TableCell>}
                    {colVisible('banco') && <TableCell className="text-xs text-slate-600">{record.bank_name || '-'}</TableCell>}
                    {colVisible('categoria') && <TableCell className="text-xs text-slate-600">{record.category_name || '-'}</TableCell>}
                    {colVisible('referencia') && <TableCell className="text-slate-700">{new Date(record.reference_date + 'T00:00:00').toLocaleDateString('pt-BR')}</TableCell>}
                    {colVisible('vencimento') && <TableCell className="text-slate-700">{record.due_date ? new Date(record.due_date + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>}
                    {colVisible('pagamento_data') && <TableCell className="text-slate-700">{record.payment_date ? new Date(record.payment_date + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>}
                    {colVisible('observacao') && <TableCell className="text-xs text-slate-600 max-w-[160px] truncate" title={record.observation || ''}>{record.observation || '-'}</TableCell>}
                    {colVisible('acoes') && (
                      <TableCell className="text-right py-2">
                        <div className="flex items-center justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                data-testid={`actions-menu-${record.record_id}`}
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-lg border-blue-900/20 text-blue-900 hover:bg-blue-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors"
                                title="Ações do registro"
                              >
                                <FiMoreVertical size={16} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-36 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-md">
                              <DropdownMenuItem
                                data-testid={`edit-record-${record.record_id}`}
                                onClick={() => handleEdit(record)}
                                className="flex items-center gap-2 cursor-pointer text-slate-700 dark:text-slate-200 focus:bg-blue-50 dark:focus:bg-blue-950/40 focus:text-blue-900 dark:focus:text-blue-300"
                              >
                                <FiEdit2 size={14} className="text-blue-600 dark:text-blue-400" />
                                <span>Editar</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-testid={`duplicate-record-${record.record_id}`}
                                onClick={() => handleDuplicate(record)}
                                className="flex items-center gap-2 cursor-pointer text-slate-700 dark:text-slate-200 focus:bg-blue-50 dark:focus:bg-blue-950/40 focus:text-blue-900 dark:focus:text-blue-300"
                              >
                                <FiCopy size={14} className="text-blue-600 dark:text-blue-400" />
                                <span>Duplicar</span>
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="my-1 bg-slate-100 dark:bg-slate-800" />
                              <DropdownMenuItem
                                data-testid={`delete-record-${record.record_id}`}
                                onClick={() => handleDelete(record.record_id)}
                                className="flex items-center gap-2 cursor-pointer text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-950/40 focus:text-red-700 dark:focus:text-red-300"
                              >
                                <FiTrash2 size={14} />
                                <span>Excluir</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
            </TableBody>
          </Table>
        </div>
      </div>
      </main>
    </div>
  );
};

// --- Helpers (SelectWithAdd & CrudListDialog moved to /components) ---